import EmblaCarousel from 'embla-carousel';

import './view.scss';

function initTestimonialCarousel(viewport: HTMLElement): void {
  const block = viewport.closest<HTMLElement>('.wp-block-interact-testimonial-carousel');
  if (!block) return;

  const container = viewport.querySelector<HTMLElement>('.embla__container');
  if (!container) return;

  const slideCount = container.children.length;
  if (slideCount <= 1) return;

  const autoAdvance = block.getAttribute('data-auto-advance') === 'true';
  const intervalSec = parseFloat(block.getAttribute('data-auto-advance-interval') || '6');
  // Skip looping when there are only 2 slides — Embla clones them which can
  // cause a brief layout shift.
  const enableLoop = slideCount > 2;

  const embla = EmblaCarousel(viewport, {
    loop: enableLoop,
    align: 'center',
    slidesToScroll: 1,
    skipSnaps: false,
  });

  // ---------- Dots ----------
  const dots = document.createElement('div');
  dots.className = 'testimonial-carousel__dots';
  dots.setAttribute('role', 'tablist');
  dots.setAttribute('aria-label', 'Testimonial pagination');
  const stage = viewport.closest<HTMLElement>('.testimonial-carousel__stage');
  (stage ?? viewport).after(dots);

  const buildDots = () => {
    const snaps = embla.scrollSnapList();
    dots.innerHTML = '';
    snaps.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'testimonial-carousel__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Show testimonial ${i + 1}`);
      dot.addEventListener('click', () => embla.scrollTo(i), false);
      dots.appendChild(dot);
    });
    updateDots();
  };

  const updateDots = () => {
    const selected = embla.selectedScrollSnap();
    dots.querySelectorAll<HTMLElement>('.testimonial-carousel__dot').forEach((dot, i) => {
      const active = i === selected;
      dot.classList.toggle('testimonial-carousel__dot--active', active);
      dot.setAttribute('aria-selected', String(active));
    });
  };

  embla.on('select', updateDots);
  embla.on('reInit', buildDots);
  buildDots();

  // ---------- Auto-advance ----------
  if (!autoAdvance || intervalSec <= 0) return;

  // Skip auto-advance for users who prefer reduced motion (WCAG 2.2.2).
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reducedMotion.matches) return;

  const ms = intervalSec * 1000;
  let timer: number | undefined;

  const isInteracting = () => block.matches(':hover, :focus-within');

  const start = () => {
    stop();
    if (isInteracting() || document.hidden) return;
    timer = window.setInterval(() => embla.scrollNext(), ms);
  };
  const stop = () => {
    if (timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };

  const onVisibility = () => {
    if (document.hidden) stop();
    else start();
  };

  // Listeners on the block — auto-cleaned when its subtree is removed,
  // and we explicitly tear down on Embla `destroy`.
  block.addEventListener('mouseenter', stop);
  block.addEventListener('mouseleave', start);
  block.addEventListener('focusin', stop);
  block.addEventListener('focusout', start);
  document.addEventListener('visibilitychange', onVisibility);

  embla.on('destroy', () => {
    stop();
    document.removeEventListener('visibilitychange', onVisibility);
    block.removeEventListener('mouseenter', stop);
    block.removeEventListener('mouseleave', start);
    block.removeEventListener('focusin', stop);
    block.removeEventListener('focusout', start);
  });

  start();
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll<HTMLElement>('[data-testimonial-viewport="true"]')
    .forEach(initTestimonialCarousel);
});
